/**
 * Face validation utilities using MediaPipe FaceDetector and FaceLandmarker.
 * Validates captured selfies against quality, pose, and centering rules.
 */

const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm';
const FACE_DETECTOR_MODEL =
	'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';
const FACE_LANDMARKER_MODEL =
	'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

const EYE_BLINK_THRESHOLD = 0.4;
const MIN_FACE_WIDTH_PX = 128;
const POSE_PITCH_DEG = 18;
const POSE_YAW_DEG = 20;
const POSE_ROLL_DEG = 15;
const CENTER_MARGIN = 0.12;
const FAR_ENOUGH_THRESHOLD = 0.55; // face bbox must be <= smallestResolution * (1 - 2*0.225)

const ERROR_KEYS = {
	camNoFace: 'camNoFace',
	camMultipleFaces: 'camMultipleFaces',
	camFaceTooSmall: 'camFaceTooSmall',
	camFaceTooLarge: 'camFaceTooLarge',
	camEyesClosed: 'camEyesClosed',
	camPoseInvalid: 'camPoseInvalid',
	camFaceNotCentered: 'camFaceNotCentered',
};

/**
 * Extract pitch, yaw, roll in degrees from facial transformation matrix (4x4).
 * Logic adapted from pose extraction (Euler angles from transformation matrix).
 */
function extractPoseFromMatrix(matrixData) {
	if (!matrixData || matrixData.length < 11) {
		return { pitch: 0, yaw: 0, roll: 0 };
	}
	const e = matrixData;
	const row0Len = Math.sqrt(e[0] * e[0] + e[1] * e[1] + e[2] * e[2]);
	const row1Len = Math.sqrt(e[4] * e[4] + e[5] * e[5] + e[6] * e[6]);
	const row2Len = Math.sqrt(e[8] * e[8] + e[9] * e[9] + e[10] * e[10]);
	if (row0Len === 0 || row1Len === 0 || row2Len === 0) {
		return { pitch: 0, yaw: 0, roll: 0 };
	}
	const i = [
		e[0] / row0Len,
		e[1] / row1Len,
		e[2] / row2Len,
		e[4] / row0Len,
		e[5] / row1Len,
		e[6] / row2Len,
		e[8] / row0Len,
		e[9] / row1Len,
		e[10] / row2Len,
	];
	const pitch = Math.asin(-i[7]);
	const yaw = Math.atan2(i[6], i[8]);
	const roll = Math.atan2(i[1], i[4]);
	return {
		pitch: (pitch * 180) / Math.PI,
		yaw: (yaw * 180) / Math.PI,
		roll: (roll * 180) / Math.PI,
	};
}

/**
 * Check if pose exceeds limits (pitch ±18°, yaw ±20°, roll ±15°).
 */
function isPoseInvalid(matrixData) {
	const { pitch, yaw, roll } = extractPoseFromMatrix(matrixData);
	return Math.abs(pitch) > POSE_PITCH_DEG || Math.abs(yaw) > POSE_YAW_DEG || Math.abs(roll) > POSE_ROLL_DEG;
}

/**
 * Check if eyes are closed (blendshape score > 0.4).
 */
function hasEyesClosed(faceBlendshapes) {
	if (!faceBlendshapes?.categories) return false;
	const eyeCats = faceBlendshapes.categories.filter(
		(c) => (c.categoryName === 'eyeBlinkLeft' || c.categoryName === 'eyeBlinkRight') && c.score > EYE_BLINK_THRESHOLD,
	);
	return eyeCats.length > 0;
}

/**
 * FarEnough: face too large if width or height > smallestResolution * 0.55.
 */
function isFaceTooLarge(bbox, smallestResolution) {
	const maxDim = smallestResolution * FAR_ENOUGH_THRESHOLD;
	return bbox.width > maxDim || bbox.height > maxDim;
}

/**
 * Initialize FaceDetector and FaceLandmarker. Returns { faceDetector, faceLandmarker }.
 */
export async function createFaceRecognitionTools() {
	const { FilesetResolver, FaceDetector, FaceLandmarker } = await import('@mediapipe/tasks-vision');

	const vision = await FilesetResolver.forVisionTasks(WASM_BASE);

	const faceDetector = await FaceDetector.createFromOptions(vision, {
		baseOptions: { modelAssetPath: FACE_DETECTOR_MODEL },
		runningMode: 'IMAGE',
	});

	const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
		baseOptions: { modelAssetPath: FACE_LANDMARKER_MODEL },
		runningMode: 'IMAGE',
		outputFaceBlendshapes: true,
		outputFacialTransformationMatrixes: true,
	});

	return { faceDetector, faceLandmarker };
}

/**
 * Validate a captured image blob. Runs FaceDetector + FaceLandmarker in IMAGE mode.
 * @param {Blob} blob - Captured image
 * @param {Object} tools - { faceDetector, faceLandmarker } from createFaceRecognitionTools
 * @param {number} imageWidth - Full image width (for centering/far-enough checks)
 * @param {number} imageHeight - Full image height
 * @returns {Promise<{ accepted: boolean, reason?: string, faceBoundingBox?: Object }>}
 */
export async function validateFace(blob, tools, imageWidth, imageHeight) {
	const { faceDetector, faceLandmarker } = tools;
	if (!faceDetector || !faceLandmarker) {
		return { accepted: false, reason: ERROR_KEYS.camNoFace };
	}

	const img = await createImageFromBlob(blob);
	if (!img) {
		return { accepted: false, reason: ERROR_KEYS.camNoFace };
	}

	const smallestResolution = Math.min(imageWidth, imageHeight);

	const faceDetections = faceDetector.detect(img);
	const detections = faceDetections.detections || [];

	if (detections.length === 0) {
		return { accepted: false, reason: ERROR_KEYS.camNoFace };
	}
	if (detections.length > 1) {
		return { accepted: false, reason: ERROR_KEYS.camMultipleFaces };
	}

	const bbox = detections[0].boundingBox;
	if (!bbox) {
		return { accepted: false, reason: ERROR_KEYS.camNoFace };
	}

	if (bbox.width < MIN_FACE_WIDTH_PX) {
		return { accepted: false, reason: ERROR_KEYS.camFaceTooSmall };
	}

	if (isFaceTooLarge(bbox, smallestResolution)) {
		return { accepted: false, reason: ERROR_KEYS.camFaceTooLarge };
	}

	const size = smallestResolution;
	const centerLeft = (imageWidth - size) / 2;
	const centerTop = (imageHeight - size) / 2;
	const margin = size * CENTER_MARGIN;
	const innerLeft = centerLeft + margin;
	const innerTop = centerTop + margin;
	const innerRight = centerLeft + size - margin;
	const innerBottom = centerTop + size - margin;
	const inside =
		bbox.originX >= innerLeft &&
		bbox.originY >= innerTop &&
		bbox.originX + bbox.width <= innerRight &&
		bbox.originY + bbox.height <= innerBottom;
	if (!inside) {
		return { accepted: false, reason: ERROR_KEYS.camFaceNotCentered };
	}

	const landmarkResult = faceLandmarker.detect(img);

	if (landmarkResult.faceBlendshapes?.length > 0 && hasEyesClosed(landmarkResult.faceBlendshapes[0])) {
		return { accepted: false, reason: ERROR_KEYS.camEyesClosed };
	}

	if (landmarkResult.facialTransformationMatrixes?.length > 0) {
		const matrixData = landmarkResult.facialTransformationMatrixes[0].data;
		if (matrixData && isPoseInvalid(matrixData)) {
			return { accepted: false, reason: ERROR_KEYS.camPoseInvalid };
		}
	}

	return { accepted: true, faceBoundingBox: bbox };
}

/**
 * Create HTMLImageElement from Blob for MediaPipe detection.
 */
function createImageFromBlob(blob) {
	return new Promise((resolve) => {
		const img = new Image();
		const url = URL.createObjectURL(blob);
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve(img);
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			resolve(null);
		};
		img.src = url;
	});
}

export { ERROR_KEYS };
